// Who may hand a customer's enquiry over (#1385). The forwarding panel of Settings → Email (#1432).
//
// Customers keep writing to the addresses they already have — an owner's private mailbox, an
// operator's old address from a business card. Forwarding that mail in is the only way to get it
// here, and without this list the platform answers the colleague instead of the customer.
//
// An address here is an authorization, not a preference: it lets the platform open a conversation
// with a third party under this business's name. The card says so rather than hiding it, and the
// screen it lives on is admin-only — but the enforcement is `/api/trusted-forwarders`, which
// re-checks the role on every route.
import React from "react";
import { Banner, Button, Card, FormField, Input } from "@/ds";
import { ApiError, cockpit, type TrustedForwarderView } from "@/api";
import { cachedRead } from "@/api/read-cache";
import { Icon, PanelLabel } from "@/lib";
import { AddressWithCopy } from "./working-address";
import { useT } from "../i18n";
import type { PushToast } from "@/notifications/toast-queue";

export interface TrustedForwardersCardProps {
  pushToast: PushToast;
  /**
   * #1432: how many addresses the list holds, each time that changes, for the card above it in
   * Settings → Email. Never called for a list that could not be read: that count is unknown, not 0.
   */
  onCount?: (count: number) => void;
}

function message(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function TrustedForwardersCard({ pushToast, onCount }: TrustedForwardersCardProps) {
  const t = useT();
  // #1536: the senders from the last visit stay on screen while they refresh.
  const [rows, setRows] = React.useState<TrustedForwarderView[] | null>(() => cachedRead("listTrustedForwarders") ?? null);
  // Only this visit's answers are reported: Settings → Email must still see a failed refresh.
  const rememberedRows = React.useRef(rows).current;
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [address, setAddress] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [target, setTarget] = React.useState<string | null>(null);

  React.useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const list = await cockpit.listTrustedForwarders();
        if (live) {
          // A read that succeeded clears an earlier failure. This effect runs again on a language
          // change, and without this the card above would stay "not available" for the rest of the
          // visit — including after an address was added through the very list it is counting.
          setLoadFailed(false);
          setRows(list);
        }
      } catch (failure) {
        if (live) {
          // The failure is marked before the empty list, because these renders are not batched:
          // the other order leaves one render holding an empty list nobody has failed to read,
          // and the count reported from it would say "no senders" where it is simply unknown.
          setLoadFailed(true);
          setRows([]);
          setError(message(failure, t("forwarders.loadFailed")));
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [t]);

  // Read through a ref so a parent passing a fresh callback each render cannot re-run this.
  const onCountRef = React.useRef(onCount);
  onCountRef.current = onCount;
  React.useEffect(() => {
    if (rows !== null && rows !== rememberedRows && !loadFailed) onCountRef.current?.(rows.length);
  }, [rows, rememberedRows, loadFailed]);

  // Where the forward has to go (#1388). The same address and the same copy affordance Settings →
  // System shows, read through the same seam, so the two screens cannot drift apart in wording.
  // A business with no live address, or a read that fails, sees the card exactly as before: this
  // is informational, it grants nothing, and the list below works without it.
  React.useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const mailbox = await cockpit.tenantNeoMailbox();
        if (live) setTarget(mailbox.emailAddress);
      } catch {
        // Deliberately silent — see above.
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const add = async () => {
    setError(null);
    setBusy(true);
    try {
      const row = await cockpit.addTrustedForwarder(address, label.trim() || null);
      // The server answers with the row that holds the address, which for a repeated add is the
      // existing one — so replace by id rather than appending and showing it twice.
      setRows((current) => [...(current ?? []).filter((r) => r.id !== row.id), row]);
      setAddress("");
      setLabel("");
      pushToast({ tone: "success", title: t("forwarders.added", { address: row.address }) });
    } catch (failure) {
      setError(message(failure, t("forwarders.addFailed")));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: TrustedForwarderView) => {
    setError(null);
    setBusy(true);
    try {
      await cockpit.removeTrustedForwarder(row.id);
      setRows((current) => (current ?? []).filter((r) => r.id !== row.id));
      pushToast({ tone: "success", title: t("forwarders.removed", { address: row.address }) });
    } catch (failure) {
      setError(message(failure, t("forwarders.removeFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="trusted-forwarders">
      <PanelLabel icon="mail">{t("forwarders.title")}</PanelLabel>
      <p className="subtle management-copy" style={{ marginTop: 0 }}>{t("forwarders.intro")}</p>
      {target && (
        <>
          <p className="subtle management-copy">{t("forwarders.target")}</p>
          <AddressWithCopy address={target} />
        </>
      )}
      <p className="subtle management-copy">{t("forwarders.security")}</p>
      {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}

      {rows !== null && (rows.length === 0
        ? <p className="subtle management-copy" data-testid="trusted-forwarders-empty">{t("forwarders.empty")}</p>
        : (
          <ul className="stack-sm" data-testid="trusted-forwarders-list" style={{ listStyle: "none", padding: 0 }}>
            {rows.map((row) => (
              <li key={row.id} className="management-actions" data-testid="trusted-forwarder-row">
                <span data-testid="trusted-forwarder-address">{row.originalAddress}</span>
                {row.label && <span className="subtle">{row.label}</span>}
                <Button
                  variant="ghost"
                  disabled={busy}
                  aria-label={t("forwarders.removeAria", { address: row.originalAddress })}
                  onClick={() => { void remove(row); }}
                >
                  {t("forwarders.remove")}
                </Button>
              </li>
            ))}
          </ul>
        ))}

      <div className="stack-sm">
        <FormField label={t("forwarders.addressLabel")} htmlFor="trusted-forwarder-address" required>
          <Input
            id="trusted-forwarder-address"
            type="email"
            value={address}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAddress(event.currentTarget.value)}
          />
        </FormField>
        <FormField label={t("forwarders.labelLabel")} htmlFor="trusted-forwarder-label">
          <Input
            id="trusted-forwarder-label"
            value={label}
            placeholder={t("forwarders.labelPlaceholder")}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLabel(event.currentTarget.value)}
          />
        </FormField>
        <div className="management-actions">
          <Button
            loading={busy}
            variant="primary"
            iconLeft={<Icon name="plus" size={16} />}
            disabled={busy || !address.trim()}
            onClick={() => { void add(); }}
          >
            {t("forwarders.add")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
